import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listLeads, createLead, updateLead, deleteLead, convertLead } from '../services/api';
import ListCard from '../components/ListCard';
import ClientFields, { EMPTY_CLIENT, clientPayload } from '../components/ClientFields';
import { apiErrorMessage } from '../utils/apiError';
import useMediaQuery, { MOBILE_QUERY } from '../hooks/useMediaQuery';

const STAGES = ['lead', 'prospect', 'qualified', 'converted', 'lost'];
const STAGE_COLORS = {
  lead: '#6c757d',
  prospect: '#0d6efd',
  qualified: '#198754',
  converted: '#1a3c6e',
  lost: '#dc3545',
};

export default function LeadsPage() {
  const { t } = useTranslation();
  const isPhone = useMediaQuery(MOBILE_QUERY);
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStage, setFilterStage] = useState('');

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', company_name: '',
    address: '', source: '', notes: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Convert modal
  const [convertingLead, setConvertingLead] = useState(null);
  // The client created by the conversion, prefilled from the lead, completed here.
  const [convertClient, setConvertClient] = useState(EMPTY_CLIENT);
  const [convertError, setConvertError] = useState('');
  const [convertForm, setConvertForm] = useState({
    create_appointment: false, appointment_title: '', appointment_date: '',
  });

  useEffect(() => {
    listLeads(filterStage ? { stage: filterStage } : {})
      .then(setLeads)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filterStage]);

  const resetForm = () => {
    setForm({ full_name: '', email: '', phone: '', company_name: '', address: '', source: '', notes: '' });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { setError(t('clients.nameRequired')); return; }
    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateLead(editingId, form);
        setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      } else {
        const created = await createLead(form);
        setLeads((prev) => [created, ...prev]);
      }
      resetForm();
    } catch (err) { setError(apiErrorMessage(err, t)); }
    finally { setSaving(false); }
  };

  const handleEdit = (lead) => {
    setForm({
      full_name: lead.full_name, email: lead.email, phone: lead.phone,
      company_name: lead.company_name, address: lead.address,
      source: lead.source, notes: lead.notes,
    });
    setEditingId(lead.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('leads.deleteConfirm'))) return;
    await deleteLead(id);
    setLeads((prev) => prev.filter((l) => l.id !== id));
  };

  const handleStageChange = async (lead, newStage) => {
    const updated = await updateLead(lead.id, { stage: newStage });
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  };

  const handleConvert = async () => {
    if (!convertingLead) return;
    try {
      if (!convertClient.name.trim()) { setConvertError(t('clients.nameRequired')); return; }
      const payload = {
        ...convertForm,
        appointment_date: convertForm.appointment_date || null,
        customer: clientPayload({ ...convertClient, name: convertClient.name.trim() }),
      };
      const result = await convertLead(convertingLead.id, payload);
      // Refresh list
      const refreshed = await listLeads(filterStage ? { stage: filterStage } : {});
      setLeads(refreshed);
      setConvertingLead(null);
      setConvertForm({ create_appointment: true, appointment_title: '', appointment_date: '' });
      // Navigate to the appointment if one was created
      if (result.appointment) {
        navigate('/appointments');
      }
    } catch (err) {
      setConvertError(apiErrorMessage(err, t));
    }
  };

  const handleConvertAndSchedule = (lead) => {
    setConvertClient({
      ...EMPTY_CLIENT,
      name: lead.full_name || '',
      contact_person: lead.full_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
    });
    setConvertError('');
    setConvertForm({
      create_appointment: true,
      appointment_title: lead.full_name,
      appointment_date: '',
    });
    setConvertingLead(lead);
  };

  if (loading) return <p style={{ textAlign: 'center', paddingTop: 40 }}>{t('auth.loading')}</p>;

  const stageBadge = (stage) => (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 12, fontWeight: 600, color: '#fff',
      background: STAGE_COLORS[stage] || '#888',
    }}>
      {t(`leads.stage_${stage}`)}
    </span>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">{t('leads.title')}</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
          + {t('leads.create')}
        </button>
      </div>

      {/* Stage filter tabs */}
      <div className="leads-stage-tabs">
        <button
          className={`leads-stage-tab ${filterStage === '' ? 'active' : ''}`}
          onClick={() => { setLoading(true); setFilterStage(''); }}
        >
          {t('leads.allStages')}
        </button>
        {STAGES.map((s) => (
          <button
            key={s}
            className={`leads-stage-tab ${filterStage === s ? 'active' : ''}`}
            style={{ '--tab-color': STAGE_COLORS[s] }}
            onClick={() => { setLoading(true); setFilterStage(s); }}
          >
            {t(`leads.stage_${s}`)}
          </button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
            {editingId ? t('leads.edit') : t('leads.create')}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>{t('leads.fullName')} *</label>
                <input className="form-control" value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>{t('leads.companyName')}</label>
                <input className="form-control" value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>{t('leads.email')}</label>
                <input className="form-control" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>{t('leads.phone')}</label>
                <input className="form-control" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label>{t('leads.source')}</label>
                <input className="form-control" value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  placeholder={t('leads.sourcePlaceholder')} />
              </div>
            </div>
            <div className="form-group">
              <label>{t('leads.address')}</label>
              <input className="form-control" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('leads.notes')}</label>
              <textarea className="form-control" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            {error && <p style={{ color: 'var(--color-danger)', fontSize: 13 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? t('auth.loading') : (editingId ? t('wizard.save') : t('leads.create'))}
              </button>
              <button className="btn btn-secondary" type="button" onClick={resetForm}>{t('wizard.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Convert modal */}
      {convertingLead && (
        <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--color-primary)' }}>
          <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 700 }}>
            {t('leads.convertTitle')} : {convertingLead.full_name}
          </h3>
          <p style={{ fontSize: 13, color: 'var(--color-text-light)', marginBottom: 16 }}>
            {t('leads.convertDescription')}
          </p>
          <div style={{ marginBottom: 20 }}>
            <ClientFields value={convertClient} onChange={setConvertClient} idPrefix="convert" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>{t('appointments.appointmentTitle')}</label>
              <input className="form-control" value={convertForm.appointment_title}
                onChange={(e) => setConvertForm({ ...convertForm, appointment_title: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('appointments.scheduledAt')}</label>
              <input className="form-control" type="datetime-local" value={convertForm.appointment_date}
                onChange={(e) => setConvertForm({ ...convertForm, appointment_date: e.target.value })} />
            </div>
          </div>
          {convertError && <div className="ai-error" role="alert" style={{ marginBottom: 12 }}>{convertError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleConvert}>
              {t('leads.convertAndSchedule')}
            </button>
            <button className="btn btn-secondary" onClick={() => setConvertingLead(null)}>
              {t('wizard.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {leads.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">&#128100;</div>
          <p>{t('leads.empty')}</p>
        </div>
      ) : isPhone ? (
        <div className="list-cards">
          {leads.map((lead) => {
            const open = lead.stage !== 'converted' && lead.stage !== 'lost';
            return (
              <ListCard
                key={lead.id}
                accent={STAGE_COLORS[lead.stage]}
                title={lead.full_name}
                badge={stageBadge(lead.stage)}
                meta={[lead.company_name, lead.source]}
                secondary={
                  (lead.email || lead.phone) && (
                    <>
                      {/* Tapping calls or writes — the point of having it on a phone. */}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()}>
                          {lead.phone}
                        </a>
                      )}
                      {lead.phone && lead.email && <span className="list-card-sep"> · </span>}
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} onClick={(e) => e.stopPropagation()}>
                          {lead.email}
                        </a>
                      )}
                    </>
                  )
                }
                onClick={() => handleEdit(lead)}
                actions={
                  <>
                    {open && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleConvertAndSchedule(lead)}
                      >
                        {t('leads.convertAndSchedule')}
                      </button>
                    )}
                    {open && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleStageChange(lead, 'lost')}
                      >
                        {t('leads.markLost')}
                      </button>
                    )}
                  </>
                }
                destructive={
                  <button
                    className="btn btn-ghost btn-sm"
                    title={t('leads.delete')}
                    aria-label={t('leads.delete')}
                    onClick={() => handleDelete(lead.id)}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                }
              />
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('leads.fullName')}</th>
                  <th>{t('leads.companyName')}</th>
                  <th>{t('leads.email')}</th>
                  <th>{t('leads.phone')}</th>
                  <th>{t('leads.source')}</th>
                  <th>{t('leads.stage')}</th>
                  <th>{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id}>
                    <td style={{ fontWeight: 600 }}>{lead.full_name}</td>
                    <td style={{ fontSize: 13 }}>{lead.company_name || '\u2014'}</td>
                    <td style={{ fontSize: 13 }}>{lead.email || '\u2014'}</td>
                    <td style={{ fontSize: 13 }}>{lead.phone || '\u2014'}</td>
                    <td style={{ fontSize: 13 }}>{lead.source || '\u2014'}</td>
                    <td>{stageBadge(lead.stage)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {lead.stage !== 'converted' && lead.stage !== 'lost' && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleConvertAndSchedule(lead)}
                          >
                            {t('leads.convertAndSchedule')}
                          </button>
                        )}
                        {lead.stage !== 'converted' && lead.stage !== 'lost' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleStageChange(lead, 'lost')}
                          >
                            {t('leads.markLost')}
                          </button>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(lead)}>
                          {t('admin.edit')}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(lead.id)}>
                          {t('leads.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
